import Swal from 'sweetalert2'

/**
 * Utilidades para confirmaciones y alertas usando SweetAlert2
 * Proporciona una interfaz consistente para todo el sistema
 */
export const ConfirmationUtils = {
  /**
   * Confirmación genérica para acciones importantes
   * @param {Object} options - Opciones de configuración
   * @param {string} options.title - Título del modal
   * @param {string} options.text - Texto del modal
   * @param {string} options.icon - Icono ('warning', 'question', 'error', etc.)
   * @param {string} options.confirmButtonText - Texto del botón confirmar
   * @param {string} options.confirmButtonColor - Color del botón confirmar
   * @returns {Promise<boolean>} - True si el usuario confirma
   */
  async confirm(options = {}) {
    const defaultOptions = {
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        confirmButton: 'swal-custom-confirm',
        cancelButton: 'swal-custom-cancel'
      }
    }

    const result = await Swal.fire({ ...defaultOptions, ...options })
    return result.isConfirmed
  },

  /**
   * Confirmación para eliminar elementos
   * @param {string} itemName - Nombre del elemento a eliminar
   * @param {string} itemType - Tipo de elemento (empresa, usuario, etc.)
   * @returns {Promise<boolean>}
   */
  async confirmDelete(itemName, itemType = 'elemento') {
    return this.confirm({
      title: `¿Eliminar ${itemType}?`,
      html: `¿Estás seguro de que deseas eliminar ${itemType === 'elemento' ? 'este' : 'la'} ${itemType} <strong>"${itemName}"</strong>?<br><br><small class="text-danger">Esta acción no se puede deshacer.</small>`,
      icon: 'warning',
      confirmButtonText: 'Sí, eliminar',
      confirmButtonColor: '#ef4444'
    })
  },

  /**
   * Confirmación para cambiar estado
   * @param {string} itemName - Nombre del elemento
   * @returns {Promise<boolean>}
   */
  async confirmStatusChange(itemName) {
    return this.confirm({
      title: '¿Cambiar estado?',
      html: `¿Estás seguro de que deseas cambiar el estado de <strong>"${itemName}"</strong>?`,
      icon: 'question',
      confirmButtonText: 'Sí, cambiar estado',
      confirmButtonColor: '#0ea5e9'
    })
  },

  /**
   * Confirmación para acciones destructivas
   * @param {string} action - Acción a realizar
   * @param {string} itemName - Nombre del elemento
   * @param {string} warning - Mensaje de advertencia adicional
   * @returns {Promise<boolean>}
   */
  async confirmDestructiveAction(action, itemName, warning = '') {
    return this.confirm({
      title: `¿${action}?`,
      html: `¿Estás seguro de que deseas ${action.toLowerCase()} <strong>"${itemName}"</strong>?${warning ? `<br><br><small class="text-danger">${warning}</small>` : ''}`,
      icon: 'warning',
      confirmButtonText: `Sí, ${action.toLowerCase()}`,
      confirmButtonColor: '#f59e0b'
    })
  },

  /**
   * Mostrar información importante
   * @param {Object} options - Opciones de configuración
   */
  async showInfo(options = {}) {
    const defaultOptions = {
      icon: 'info',
      confirmButtonColor: '#0ea5e9',
      confirmButtonText: 'Entendido',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        confirmButton: 'swal-custom-confirm'
      }
    }

    return Swal.fire({ ...defaultOptions, ...options })
  },

  /**
   * Mostrar éxito
   * @param {Object} options - Opciones de configuración
   */
  async showSuccess(options = {}) {
    const defaultOptions = {
      icon: 'success',
      confirmButtonColor: '#10b981',
      confirmButtonText: 'Perfecto',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        confirmButton: 'swal-custom-confirm'
      }
    }

    return Swal.fire({ ...defaultOptions, ...options })
  },

  /**
   * Mostrar error
   * @param {Object} options - Opciones de configuración
   */
  async showError(options = {}) {
    const defaultOptions = {
      icon: 'error',
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Entendido',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        confirmButton: 'swal-custom-confirm'
      }
    }

    return Swal.fire({ ...defaultOptions, ...options })
  }
}

export default ConfirmationUtils